import {
  mdiContain,
  mdiEqual,
  mdiNotEqual,
  mdiGreaterThan,
  mdiLessThan,
  mdiGreaterThanOrEqual,
  mdiLessThanOrEqual,
} from "@mdi/js";
import { ColumnType } from "./types";
import { FilterOperator } from "./hooks/useFiltering";
import { TypographyContent } from "../../atoms/Typography";

// ============ Filter Constants ============

export enum BoolValue {
  TRUE = "true",
  FALSE = "false",
}

export const NULL_FILTER_VALUE = "__NULL__";

export enum TextFilterMode {
  STARTS_WITH = "startsWith",
  ENDS_WITH = "endsWith",
  EQUALS = "equals",
  CONTAINS = "contains",
}

export const TEXT_FILTER_PREFIXES: Record<TextFilterMode, string> = {
  [TextFilterMode.STARTS_WITH]: "^",
  [TextFilterMode.ENDS_WITH]: "*",
  [TextFilterMode.EQUALS]: "=",
  [TextFilterMode.CONTAINS]: "~",
};

export interface FilterOperatorDef {
  id: FilterOperator;
  label: TypographyContent;
  iconPath?: string;
}

/** Filter operators by column type */
export const FILTER_OPERATORS: Record<string, FilterOperatorDef[]> = {
  [ColumnType.TEXT]: [{ id: "=", label: { local: "::PSS::Filter::Op::Contains" }, iconPath: mdiContain }],
  [ColumnType.INTEGER]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: "!=", label: { local: "::PSS::Filter::Op::NotEquals" }, iconPath: mdiNotEqual },
    { id: ">", label: { local: "::PSS::Filter::Op::GreaterThan" }, iconPath: mdiGreaterThan },
    { id: "<", label: { local: "::PSS::Filter::Op::LessThan" }, iconPath: mdiLessThan },
    { id: ">=", label: { local: "::PSS::Filter::Op::GreaterOrEqual" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::LessOrEqual" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.FLOAT]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: "!=", label: { local: "::PSS::Filter::Op::NotEquals" }, iconPath: mdiNotEqual },
    { id: ">", label: { local: "::PSS::Filter::Op::GreaterThan" }, iconPath: mdiGreaterThan },
    { id: "<", label: { local: "::PSS::Filter::Op::LessThan" }, iconPath: mdiLessThan },
    { id: ">=", label: { local: "::PSS::Filter::Op::GreaterOrEqual" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::LessOrEqual" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.DATE]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: ">=", label: { local: "::PSS::Filter::Op::OnOrAfter" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::OnOrBefore" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.LOCALDATE]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: ">=", label: { local: "::PSS::Filter::Op::OnOrAfter" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::OnOrBefore" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.DATETIME]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: ">=", label: { local: "::PSS::Filter::Op::OnOrAfter" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::OnOrBefore" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.TIME]: [
    { id: "=", label: { local: "::PSS::Filter::Op::Equals" }, iconPath: mdiEqual },
    { id: ">=", label: { local: "::PSS::Filter::Op::AtOrAfter" }, iconPath: mdiGreaterThanOrEqual },
    { id: "<=", label: { local: "::PSS::Filter::Op::AtOrBefore" }, iconPath: mdiLessThanOrEqual },
  ],
  [ColumnType.ENUM]: [{ id: "=", label: { local: "::PSS::Filter::Op::Is" }, iconPath: mdiEqual }],
  [ColumnType.LOCALIZED]: [{ id: "=", label: { local: "::PSS::Filter::Op::Is" }, iconPath: mdiEqual }],
  [ColumnType.BOOL]: [{ id: "=", label: { local: "::PSS::Filter::Op::Is" }, iconPath: mdiEqual }],
};

// ============ Pagination Constants ============

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ============ Column Type Defaults ============

/** Default alignment by column type */
export const COLUMN_TYPE_ALIGNMENTS: Record<
  ColumnType,
  "left" | "center" | "right"
> = {
  [ColumnType.TEXT]: "left",
  [ColumnType.BOOL]: "center",
  [ColumnType.DATE]: "left",
  [ColumnType.LOCALDATE]: "left",
  [ColumnType.DATETIME]: "left",
  [ColumnType.TIME]: "left",
  [ColumnType.INTEGER]: "right",
  [ColumnType.FLOAT]: "right",
  [ColumnType.ENUM]: "left",
  [ColumnType.LOCALIZED]: "left",
  [ColumnType.VIRTUAL]: "left",
  [ColumnType.RENDER]: "left",
  [ColumnType.INPUT]: "left",
};

/** Column types that support filtering */
export const FILTERABLE_COLUMN_TYPES = new Set<ColumnType>([
  ColumnType.TEXT,
  ColumnType.BOOL,
  ColumnType.DATE,
  ColumnType.LOCALDATE,
  ColumnType.DATETIME,
  ColumnType.TIME,
  ColumnType.INTEGER,
  ColumnType.FLOAT,
  ColumnType.ENUM,
  ColumnType.LOCALIZED,
]);

/** Column types that support sorting */
export const SORTABLE_COLUMN_TYPES = new Set<ColumnType>([
  ColumnType.TEXT,
  ColumnType.BOOL,
  ColumnType.DATE,
  ColumnType.LOCALDATE,
  ColumnType.DATETIME,
  ColumnType.TIME,
  ColumnType.INTEGER,
  ColumnType.FLOAT,
  ColumnType.ENUM,
  ColumnType.LOCALIZED,
]);
